/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext } from 'react';
import { AuthContext } from '@/contexts/auth-context';
import { CONTROL_LEVEL } from '@/utilities/rbac/constants';

/**
 * A component that controls access to its children based on user permissions.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.permission=''] - The permission key to check against user's permissions
 * @param {string} [props.whenDenied=CONTROL_LEVEL.hide] - The behavior when permission is denied.
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode|null}
 * @example
 * // Hide content when permission is denied (default)
 * <PermissionGate permission="admin.edit">
 *   <Button>Edit</Button>
 * </PermissionGate>
 *
 * @example
 * // Disable content when permission is denied
 * <PermissionGate permission="admin.delete" whenDenied={CONTROL_LEVEL.disable}>
 *   <Button>Delete</Button>
 * </PermissionGate>
 */
export default function PermissionGate({
  permission = '',
  whenDenied = CONTROL_LEVEL.hide,
  children,
}) {
  const { hasPermission } = useContext(AuthContext);
  const allowedByRbac = permission ? hasPermission(permission) : true;

  if (allowedByRbac) return children;

  if (whenDenied === CONTROL_LEVEL.disable) {
    return React.Children.map(children, (child) => (React.isValidElement(child)
      ? React.cloneElement(child, { disabled: true })
      : child));
  }

  return null;
}
